// Cursor pagination for list endpoints: first page on mount (or when the key
// changes), "Carregar mais" appends the next page. Stale answers are dropped.
import type { Page } from '@kima-hub/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbort, userMessage } from '../api/client.js';

export interface Paged<T> {
  items: T[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

export function usePaged<T>(
  key: string | null,
  fetchPage: (cursor: string | null, signal: AbortSignal) => Promise<Page<T>>,
): Paged<T> {
  const [items, setItems] = useState<T[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;

  const load = useCallback((from: string | null, reset: boolean) => {
    const gen = generation.current;
    controller.current?.abort();
    const ctl = new AbortController();
    controller.current = ctl;
    setLoading(true);
    setError(null);
    fetchRef.current(from, ctl.signal).then(
      (page) => {
        if (gen !== generation.current) return;
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next_cursor);
        setLoading(false);
      },
      (err: unknown) => {
        if (gen !== generation.current || isAbort(err)) return;
        setError(userMessage(err));
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setCursor(null);
    if (key !== null) load(null, true);
    else setLoading(false);
    return () => {
      generation.current += 1;
      controller.current?.abort();
    };
  }, [key, load]);

  const loadMore = useCallback(() => {
    if (cursor !== null && !loading) load(cursor, false);
  }, [cursor, loading, load]);

  return { items, loading, error, hasMore: cursor !== null, loadMore };
}
